import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import { DialogActions, Divider, Step, StepLabel, Stepper } from '@mui/material';
import { log } from '@utils/logging';
import { GENERAL, STRIPE_STEP, CODE_OF_CONDUCT } from './CreateTeamSteps';
import LoadingButton from '@atoms/LoadingButton';
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form';
import { TeamForm } from './CreateTeamUtils';
import { useStripeAccountRequired } from '@hooks/useStripeAccountRequired';
import { createTeam } from '@modules/supabase/teams';
import { toast } from 'react-toastify';
import { StepIconComponent, StepperConnector } from '@components/molecules/Stepper';
import useTeams from '@hooks/useTeams';

const steps = ['General', 'Code Of Conduct', 'Payments'];

interface Props {
    step: number;
    onClose: () => void;
    onPrevClick: () => void;
    onNextClick: () => void;
}

export default function NextBackButtonGroup({ step, onClose, onNextClick, onPrevClick }: Props) {
    const { refreshTeams } = useTeams();
    // initialize stripe account if it doesn't exist already
    const { handleSubmit } = useFormContext<TeamForm>();
    const { errors } = useFormState<TeamForm>();
    useStripeAccountRequired();
    const [loading, setLoading] = useState(false);

    const onSubmit: SubmitHandler<TeamForm> = useCallback(
        (values) => {
            console.log(`CreateTeamModal onSubmit`, values);
            setLoading(true);

            // @ts-expect-error stripeDetailsSubmitted is just for validation
            delete values.stripeDetailsSubmitted;

            createTeam(
                {
                    ...values,
                    id: values.id || undefined,
                    createdAt: values.createdAt || new Date().toISOString(),
                },
                `team [NextBackButtonGroup]`,
            )
                .then((response) => {
                    if (response?.id) {
                        refreshTeams();
                        onClose();
                    } else {
                        console.warn('Error adding team', response);
                        console.error('Error adding team');
                    }
                })
                .finally(() => setLoading(false));
        },
        [onClose, refreshTeams],
    );

    const enableNext = useEnableNext(step);

    const onLastStep = step === STRIPE_STEP;

    // Some props for NextButton change depending on which step you're on
    const nextButtonProps: {
        onClick?: () => void;
        children: string;
    } = useMemo(() => {
        if (onLastStep) {
            return {
                children: 'Publish',
                onClick: () =>
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    handleSubmit(onSubmit, (_error, _event) => {
                        toast.error(`Saving team failed, please try again`);
                    })(),
                // type: 'submit',
            };
        } else {
            return {
                children: 'Next',
                onClick: onNextClick,
            };
        }
    }, [handleSubmit, onLastStep, onNextClick, onSubmit]);

    return (
        <Box>
            <Divider />
            <Box sx={{ paddingBottom: 1, paddingTop: 4, paddingX: 3 }}>
                <Stepper
                    alternativeLabel
                    activeStep={step}
                    connector={<StepperConnector />}
                    sx={{ paddingBottom: 1 }}
                >
                    {steps.length > 1
                        ? steps.map((label) => (
                              <Step key={label}>
                                  <StepLabel StepIconComponent={StepIconComponent}>
                                      {label}
                                  </StepLabel>
                              </Step>
                          ))
                        : null}
                </Stepper>
                <DialogActions>
                    <LoadingButton onClick={onClose}>Cancel</LoadingButton>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {step > 0 ? (
                        <LoadingButton type="button" onClick={onPrevClick}>
                            Prev
                        </LoadingButton>
                    ) : null}

                    <LoadingButton disabled={!enableNext} loading={loading} {...nextButtonProps} />
                </DialogActions>
                {onLastStep && Object.keys(errors).length ? (
                    <>
                        {(Object.keys(errors) as (keyof typeof errors)[]).map((errorKey) => (
                            <Box key={errorKey} sx={{ color: 'red' }}>
                                {typeof errors[errorKey]?.message === 'string'
                                    ? errors[errorKey]?.message
                                    : Array.isArray(errors[errorKey])
                                      ? errors[errorKey].map(({ message }) => message).join(', ')
                                      : null}
                            </Box>
                        ))}
                    </>
                ) : null}
            </Box>
        </Box>
    );
}

const STEP_FIELDS: { [key: number]: (keyof TeamForm)[] } = {
    [GENERAL]: ['name'],
    [CODE_OF_CONDUCT]: ['codeOfConduct'],
    [STRIPE_STEP]: ['stripeDetailsSubmitted'], // TODO: add some validation for team fee $0
};

// Exclude fields that are not required to be touched to enable the next button
const TOUCH_EXCLUDED_FIELDS: (keyof TeamForm)[] = ['stripeDetailsSubmitted'];

function useEnableNext(step: number) {
    const { errors, dirtyFields } = useFormState<TeamForm>();
    const { getValues } = useFormContext<TeamForm>();
    const values = getValues();

    const lastButtonValidation = useCallback(() => {
        if (step === STRIPE_STEP && getValues().fee !== 0) {
            return values.stripeDetailsSubmitted;
        }
        return true;
    }, [getValues, step, values.stripeDetailsSubmitted]);

    const noErrorsForStep = useCallback(
        (fields: (keyof TeamForm)[]) =>
            !fields.some((field) => Object.keys(errors).includes(field)),
        [errors],
    );

    const everyFieldTouched = useCallback(
        (fields: (keyof TeamForm)[]) =>
            values.id || fields.every((f) => dirtyFields[f] || TOUCH_EXCLUDED_FIELDS.includes(f)),
        [dirtyFields, values.id],
    );

    const stepFields = STEP_FIELDS[step];

    // console.log(`[useEnableNext] errors`, {
    //     noErrorsForStep: noErrorsForStep(stepFields),
    //     everyFieldTouched: everyFieldTouched(stepFields),
    //     lastButtonValidation: lastButtonValidation(),
    //     dirtyFields,
    //     errors,
    //     stepFields,
    // });

    if (!stepFields?.length) {
        log(`useEnableNext unrecognized step: ${step}`);
        return true;
    }

    return everyFieldTouched(stepFields) && noErrorsForStep(stepFields) && lastButtonValidation();
}
